import { useEffect, useRef, useState } from 'preact/hooks';
import { useBodyScrollLock } from 'client/lib/hooks/use-body-scroll-lock';
import { MinidocToolbarEditor } from 'minidoc-editor';
import { Cardable } from 'minidoc-editor/dist/types';
import { showError } from '@components/app-error';
import { BaseDialog, showGlobal } from '@components/dialog';
import { FilePickerOpts } from '@components/filepicker';
import { IcoChevronLeft, IcoX } from '@components/icons';
import { pickDeviceFiles } from '@components/media-capture';
import { Internationalize } from 'shared/intl';
import { captureIntoEditor, insertIntoEditor, withEditorRange } from './editor-capture';
import { parseAccept } from '@components/media-manager/use-media-manager';
import { showToast } from '@components/toaster';
import { checkForDuplicate, ManagedFile, toManagedFile } from '@components/media-manager';
import { getCurrentRange, setCurrentRange } from 'client/lib/minidoc';
import { rpx } from 'client/lib/rpx-client';
import { useIntl } from 'shared/intl/use-intl';
import { EmbedForm } from './embed-form';
import { CaptureMode, HomeView } from './home-view';
import { hasFiles } from '@components/attachments-tray/attachments';
import { LibraryView } from './library-view';
import { reviewImages } from '@components/filepicker/review-image';

export type AddMediaOpts = {
  accept?: string;
  isPublic?: boolean;
  uploadOpts?: FilePickerOpts;
  allowEmbed?: boolean;
  multiSelect?: boolean;
  enableFolders?: boolean;
  checkDuplicates?: boolean;
};

export type AddMediaTarget = {
  add(items: Array<File | ManagedFile>): void | Promise<void>;
  capture(mode: CaptureMode): void;
  embed?(content: string): void;
  cancel(): void;
};

type View = 'home' | 'library' | 'embed';

type WindowProps = {
  accept?: string;
  captures: CaptureMode[];
  uploadOpts?: FilePickerOpts;
  allowEmbed?: boolean;
  multiSelect?: boolean;
  enableFolders?: boolean;
  onClose(): void;
  onChooseFile(): void;
  onFiles(files: File[]): void;
  onCapture(mode: CaptureMode): void;
  onPickRecent(file: ManagedFile): void;
  onPickLibrary(files: ManagedFile[]): void;
  onEmbed(content: string): void;
};

function AddMediaWindow(props: WindowProps) {
  const intl = useIntl();
  const [view, setView] = useState<View>('home');
  const isLibrary = view === 'library';
  const content = useRef<HTMLDivElement>(null);
  useBodyScrollLock(true);

  useEffect(() => {
    if (view === 'home') {
      return;
    }
    const target =
      content.current?.querySelector<HTMLElement>('textarea, input') ||
      content.current?.querySelector<HTMLElement>('button');
    target?.focus();
  }, [view]);

  useEffect(() => {
    function guard(e: DragEvent) {
      if (hasFiles(e)) {
        e.preventDefault();
      }
    }
    document.addEventListener('dragover', guard);
    document.addEventListener('drop', guard);
    return () => {
      document.removeEventListener('dragover', guard);
      document.removeEventListener('drop', guard);
    };
  }, []);

  return (
    <BaseDialog
      onClose={props.onClose}
      blurBg={false}
      zIndex="z-[60]"
      contentWidth
      compact={!isLibrary}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-media-title"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && view !== 'home') {
            e.stopPropagation();
            setView('home');
          }
        }}
        onDrop={(e) => {
          if (!hasFiles(e)) {
            return;
          }
          e.preventDefault();
          const files = Array.from(e.dataTransfer?.files || []);
          if (files.length) {
            props.onFiles(files);
          }
        }}
        class={`flex flex-col flex-1 min-h-0 w-full max-w-full bg-white ${
          isLibrary ? 'sm:w-5xl sm:h-[80vh]' : 'sm:w-2xl'
        }`}
      >
        <header class="flex items-center gap-2 px-4 sm:px-6 py-3 border-b border-gray-200 shrink-0">
          {view !== 'home' && (
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md py-1 pl-0.5 pr-2 text-sm text-gray-600 hover:bg-gray-100"
              onClick={() => setView('home')}
            >
              <IcoChevronLeft class="size-4" />
              {intl('Back')}
            </button>
          )}
          <h2 id="add-media-title" class="flex-1 text-base font-semibold text-gray-900">
            {intl('Add media')}
          </h2>
          <button
            type="button"
            class="inline-flex size-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            aria-label={intl('Close')}
            onClick={props.onClose}
          >
            <IcoX class="size-[18px]" />
          </button>
        </header>
        <div ref={content} class="flex flex-col flex-1 min-h-0">
          {view === 'home' && (
            <HomeView
              accept={props.accept}
              captures={props.captures}
              allowEmbed={props.allowEmbed}
              onChooseFile={props.onChooseFile}
              onFiles={props.onFiles}
              onCapture={props.onCapture}
              onPick={props.onPickRecent}
              onBrowse={() => setView('library')}
              onEmbed={() => setView('embed')}
            />
          )}
          {isLibrary && (
            <LibraryView
              accept={props.accept}
              uploadOpts={props.uploadOpts}
              multiSelect={props.multiSelect}
              enableFolders={props.enableFolders}
              onPick={props.onPickLibrary}
              onCancel={() => setView('home')}
            />
          )}
          {view === 'embed' && (
            <EmbedForm onBack={() => setView('home')} onInsert={props.onEmbed} />
          )}
        </div>
      </div>
    </BaseDialog>
  );
}

const CAPTURE_TYPES: Record<CaptureMode, { type: string; source: string }> = {
  photo: { type: 'image/', source: 'takephoto' },
  video: { type: 'video/', source: 'takevideo' },
  audio: { type: 'audio/', source: 'takeaudio' },
};

function allowedCaptures(accept?: string, sources?: string[]) {
  const prefixes = parseAccept(accept);
  return (Object.keys(CAPTURE_TYPES) as CaptureMode[]).filter((mode) => {
    const { type, source } = CAPTURE_TYPES[mode];
    if (sources && !sources.includes(source)) {
      return false;
    }
    return !prefixes || prefixes.some((prefix) => prefix.startsWith(type));
  });
}

function isAcceptedFile(file: File, accept?: string) {
  const prefixes = parseAccept(accept);
  return !prefixes || prefixes.some((prefix) => file.type.startsWith(prefix));
}

function toInsertable(file: ManagedFile) {
  return { name: file.name, type: file.type, url: file.url };
}

export function openAddMedia(
  opts: AddMediaOpts & { intl: Internationalize },
  target: AddMediaTarget,
) {
  const { intl } = opts;

  async function addNew(files: File[]) {
    const accepted = files.filter((file) => isAcceptedFile(file, opts.accept));
    if (accepted.length < files.length) {
      showToast({
        type: 'warn',
        title: accepted.length ? intl("Some files weren't added") : intl("Files weren't added"),
        message: intl("This editor doesn't take that type of file."),
      });
    }
    const reviewed = await reviewImages(accepted);
    const chosen: Array<File | ManagedFile> = [];
    for (const file of reviewed) {
      if (opts.checkDuplicates === false) {
        chosen.push(file);
        continue;
      }
      const decision = await checkForDuplicate(file);
      if (decision.action === 'reuse') {
        chosen.push(toManagedFile(decision.result));
      } else if (decision.action === 'proceed') {
        chosen.push(file);
      }
    }
    if (!chosen.length) {
      target.cancel();
      return;
    }
    await target.add(chosen);
  }

  return showGlobal<void>(({ resolve }) => {
    function close() {
      resolve(undefined);
    }
    return (
      <AddMediaWindow
        accept={opts.accept}
        captures={allowedCaptures(opts.accept, opts.uploadOpts?.sources)}
        uploadOpts={opts.uploadOpts}
        allowEmbed={opts.allowEmbed && !!target.embed}
        multiSelect={opts.multiSelect}
        enableFolders={opts.enableFolders}
        onClose={() => {
          close();
          target.cancel();
        }}
        onChooseFile={() =>
          pickDeviceFiles({ accept: opts.accept, multiple: true }, (files) => {
            close();
            void addNew(files);
          })
        }
        onFiles={(files) => {
          close();
          void addNew(files);
        }}
        onCapture={(mode) => {
          close();
          target.capture(mode);
        }}
        onPickRecent={(file) => {
          close();
          rpx.mediaManager.markFilesImported({ fileIds: [file.id] }).catch(() => undefined);
          void target.add([file]);
        }}
        onPickLibrary={(files) => {
          close();
          if (files.length) {
            void target.add(files);
          }
        }}
        onEmbed={(content) => {
          close();
          target.embed?.(content);
        }}
      />
    );
  });
}

export function showAddMedia(
  editor: MinidocToolbarEditor,
  opts: AddMediaOpts & { intl: Internationalize },
) {
  const range = getCurrentRange(editor);

  async function makePublic(files: ManagedFile[]) {
    if (!opts.isPublic || !files.length) {
      return true;
    }
    try {
      await Promise.all(files.map((file) => rpx.files.makePublic({ id: file.id })));
      return true;
    } catch (err) {
      showError(err);
      return false;
    }
  }

  return openAddMedia(opts, {
    async add(items) {
      const reused = items.filter((item): item is ManagedFile => !(item instanceof File));
      if (!(await makePublic(reused))) {
        return;
      }
      insertIntoEditor(
        editor,
        range,
        items.map((item) => (item instanceof File ? item : toInsertable(item))),
      );
    },
    capture(mode) {
      captureIntoEditor(editor, range, mode === 'audio' ? 'voice' : mode, opts.intl);
    },
    embed(content) {
      withEditorRange(editor, range, () =>
        (editor as unknown as Cardable).insertCard('embed', { content }),
      );
    },
    cancel() {
      (editor.root as HTMLElement).focus();
      setCurrentRange(range, editor);
    },
  });
}
